import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { ApiError, apiErrorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AppShell } from '../components/AppShell';
import { fetchApplication, fetchBag } from '../lib/bag-api';
import { acceptPartialApproval, fetchMyLearners } from '../lib/registration-api';
import { dashboardPath, pathForApplication } from '../lib/selected-learner';
import { RejectedSubjectsNotes } from '../components/RejectedSubjectsNotes';
import {
  isApprovedSubject,
  isRejectedSubject,
  subjectOutcomeBadgeClass,
  subjectOutcomeLabel,
} from '../lib/subject-outcome';

export function PartialApprovalPage(): ReactElement {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => fetchApplication(applicationId),
    enabled: Boolean(token && applicationId),
  });

  const bagQuery = useQuery({
    queryKey: ['bag', applicationQuery.data?.academicBagId],
    queryFn: () => fetchBag(applicationQuery.data!.academicBagId!),
    enabled: Boolean(applicationQuery.data?.academicBagId),
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptPartialApproval(applicationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-learners'] });
      const learners = await fetchMyLearners();
      navigate(pathForApplication(learners, applicationId));
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not accept outcome');
    },
  });

  if (!loading && !token) return <Navigate to="/login" replace />;
  if (!applicationId) return <Navigate to={dashboardPath()} replace />;

  const bag = bagQuery.data;
  const approvedItems = bag?.items.filter((item) => isApprovedSubject(item)) ?? [];

  return (
    <AppShell width="wide" title="Partial approval">
      <div className="page-header">
        <p className="lede">
          Not every subject was approved. Review the outcome below. If you accept, we will
          proceed with the approved subjects only.
        </p>
      </div>

      {error ? <div className="error-banner" role="alert">{error}</div> : null}

      {applicationQuery.isLoading || bagQuery.isLoading ? (
        <p className="text-muted">Loading outcomes…</p>
      ) : null}

      {bag?.reviewComment ? (
        <div className="notice notice-info section-gap">
          <strong>Comments from the review team</strong>
          <p>{bag.reviewComment}</p>
        </div>
      ) : null}

      {bag ? (
        <div className="stack-lg">
          <div className="card card-flush">
            <div className="card-body">
              <h2 className="h3">Subject decisions</h2>
              <ul className="review-subject-list">
                {bag.items.map((item) => (
                  <li
                    key={item.id}
                    className={`review-subject-row${isRejectedSubject(item) ? ' is-rejected' : ''}`}
                  >
                    <div className="review-subject-main">
                      <div>
                        <strong>{item.subjectName ?? 'Subject'}</strong>
                        {item.syllabusCode ? (
                          <span className="subject-syllabus-code">{item.syllabusCode}</span>
                        ) : null}
                      </div>
                    </div>
                    <span className={subjectOutcomeBadgeClass(item)}>
                      {subjectOutcomeLabel(item, {
                        applicationStatus: applicationQuery.data?.status ?? '',
                        bagStatus: bag.status,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <RejectedSubjectsNotes
            subjects={bag.items.map((item) => ({
              id: item.id,
              subjectName: item.subjectName ?? 'Subject',
              syllabusCode: item.syllabusCode,
              itemStatus: item.itemStatus,
              rejectionReason: item.rejectionReason,
              rejectionComment: item.rejectionComment,
              rejectedAt: item.rejectedAt,
              reviewerParty: item.reviewerParty,
              stepName: item.stepName,
            }))}
          />
        </div>
      ) : null}

      {bag && approvedItems.length === 0 ? (
        <div className="notice notice-info section-gap">
          <strong>No subjects were approved</strong>
          <p>Contact admissions if you need to discuss next steps.</p>
        </div>
      ) : null}

      {bag && approvedItems.length > 0 ? (
        <div className="form-actions">
          <Link className="btn btn-ghost" to="/dashboard">Decide later</Link>
          <button
            className="btn btn-lg"
            type="button"
            disabled={acceptMutation.isPending}
            onClick={() => acceptMutation.mutate()}
          >
            Accept {approvedItems.length} approved subject{approvedItems.length === 1 ? '' : 's'}
          </button>
        </div>
      ) : null}
    </AppShell>
  );
}
